import { NavLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { IconLoader2, IconRepeat } from '@tabler/icons-react';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@common/utils';
import TelletLogo from '@/assets/logo/tellet-logo-main.svg';
import Illustration from '@/assets/illustrations/invitation-expired.svg';
import Image from '@/components/atoms/Image';
import { Button } from '@/components/ui/button';
import ToastMessage from '@/components/toast-message/ToastMessage';
import {
  useMutateRequestAnotherAccountInvitation,
  useMutateResendEmailInvitation,
} from '@/reactQuery/post';
import Routes from '@/router/routes';

function EmailVerificationExpired() {
  const { organization_id } = useParams();
  const navigate = useNavigate();
  const { mutateAsync: mutateAsyncResend, status: resendStatus } = useMutateResendEmailInvitation();
  const { mutateAsync, status: requestStatus } =
    useMutateRequestAnotherAccountInvitation(organization_id);
  const isLoading = resendStatus === 'pending' || requestStatus === 'pending';
  const [searchParams] = useSearchParams();
  const email = searchParams.get('inviter')
    ? decodeURIComponent(searchParams.get('inviter') || '')
    : '';
  const isInviteToOrganization = !!organization_id;

  const onSubmit = async () => {
    if (!email) {
      toast.error(
        <ToastMessage
          title="Error"
          description="Email is required to request a new verification link."
          isAuthScreen
        />,
      );
      return;
    }

    try {
      if (isInviteToOrganization) {
        // Handle organization invitation
        await mutateAsync({ email });

        // Redirect to success page with email parameter
        navigate(`${Routes.reinvite_request_sent.path}?inviter=${encodeURIComponent(email)}`);
      } else {
        // Handle email verification
        await mutateAsyncResend(email);
        toast.success(
          <ToastMessage
            title="Verification link sent"
            description="We've sent a new verification link to your email address. Please check your inbox."
            isAuthScreen
          />,
        );
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      toast.error(
        <ToastMessage
          title="Error"
          description={errorMessage || 'Something went wrong. Please try again later.'}
          isAuthScreen
        />,
      );
    }
  };

  return (
    <div className="relative flex items-center justify-center min-h-screen px-4 mx-auto text-center">
      <NavLink to="/login">
        <Image src={TelletLogo} alt="logo" className="absolute w-28 h-fit top-6 left-5" />
      </NavLink>
      <div className="flex flex-col items-center justify-center gap-2 mx-auto text-center">
        <Image
          effect="blur"
          src={Illustration}
          alt="illustration"
          className="min500:w-full w-[450px]"
        />
        <h1 className="text-5xl font-bold mt-14 text-dark min500:text-4xl">
          {isInviteToOrganization
            ? 'Your invitation link has expired'
            : 'Your email verification link has expired'}
        </h1>
        <p className="text-[#5A6A85] leading-6 mt-4 font-semibold text-xl">
          {isInviteToOrganization
            ? 'Request a new invitation by clicking the button below.'
            : 'Request a new verification link by clicking the button below.'}
        </p>

        <Button
          disabled={isLoading}
          onClick={onSubmit}
          variant="default"
          className="flex items-center gap-1.5 mt-10"
        >
          {isLoading ? (
            <IconLoader2 className="animate-spin" size={20} />
          ) : (
            <IconRepeat size={20} />
          )}
          {isInviteToOrganization
            ? 'Request Another Invitation'
            : 'Request Another Verification Link'}
        </Button>
      </div>
    </div>
  );
}

export default EmailVerificationExpired;
